import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Search,
  RefreshCw,
  Zap,
  Eye,
  Sparkles,
  BookOpen,
  ArrowRightLeft,
  TrendingUp,
  Clock,
  Copy,
  Check,
  Maximize2,
  Grid3X3,
} from 'lucide-react'

// Declare FlareSearch on window
declare global {
  interface Window {
    FlareSearch?: {
      init: (config: FlareSearchConfig) => void
      destroy: () => void
    }
  }
}

interface FlareSearchConfig {
  inputSelector: string
  apiBaseUrl: string
  mode: 'dropdown' | 'fullscreen'
  theme?: {
    primaryColor?: string
    backgroundColor?: string
    textColor?: string
    borderRadius?: string
  }
  debounceMs?: number
  minChars?: number
  maxResults?: number
}

// Feature list with icons and descriptions
const features = [
  {
    icon: Zap,
    title: 'Instant Results',
    description: 'Fast autocomplete with edge caching',
  },
  {
    icon: Sparkles,
    title: 'Typo Tolerance',
    description: 'Finds products even with spelling mistakes',
  },
  {
    icon: BookOpen,
    title: 'Smart Synonyms',
    description: 'AI-generated synonyms expand search matches',
  },
  {
    icon: Eye,
    title: 'Quick View',
    description: 'Preview products without leaving search',
  },
  {
    icon: Grid3X3,
    title: 'Similar Products',
    description: 'Shows related items in quick view',
  },
  {
    icon: TrendingUp,
    title: 'Searchandising',
    description: 'Boost, pin, and customize results',
  },
  {
    icon: ArrowRightLeft,
    title: 'Smart Redirects',
    description: 'Guide users to the right pages',
  },
  {
    icon: Clock,
    title: 'Recent Searches',
    description: 'Quick access to search history',
  },
]

export function SearchPreviewPage() {
  const [mode, setMode] = useState<'dropdown' | 'fullscreen'>('fullscreen')
  const [primaryColor, setPrimaryColor] = useState('#3b82f6')
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false)
  const [key, setKey] = useState(0)
  const [copied, setCopied] = useState(false)
  const scriptRef = useRef<HTMLScriptElement | null>(null)

  // Load widget script on mount
  useEffect(() => {
    if (document.querySelector('script[src="/widget.js"]')) {
      setIsWidgetLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/widget.js'
    script.async = true
    script.onload = () => {
      setIsWidgetLoaded(true)
    }
    script.onerror = () => {
      console.error('Failed to load widget.js')
    }
    document.body.appendChild(script)
    scriptRef.current = script

    return () => {
      if (window.FlareSearch) {
        window.FlareSearch.destroy()
      }
    }
  }, [])

  // Initialize widget when config changes
  useEffect(() => {
    if (!isWidgetLoaded || !window.FlareSearch) return

    const timer = setTimeout(() => {
      window.FlareSearch?.destroy()
      window.FlareSearch?.init({
        inputSelector: '#preview-search-input',
        apiBaseUrl: window.location.origin,
        mode,
        theme: {
          primaryColor,
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          borderRadius: '12px',
        },
        debounceMs: 150,
        minChars: 2,
        maxResults: 8,
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [isWidgetLoaded, mode, primaryColor, key])

  const handleReinitialize = () => {
    setKey((k) => k + 1)
  }

  const embedCode = `<script src="${window.location.origin}/widget.js"></script>
<script>
  FlareSearch.init({
    apiBaseUrl: '${window.location.origin}',
    mode: '${mode}',
    theme: {
      primaryColor: '${primaryColor}'
    }
  });
</script>`

  const handleCopyCode = () => {
    navigator.clipboard.writeText(embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border p-8 md:p-12">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />

        <div className="relative z-10 max-w-3xl mx-auto text-center space-y-6">
          <div className="space-y-2">
            <Badge variant="secondary" className="mb-2">
              Live Preview
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Experience Your Search
            </h1>
            <p className="text-lg text-muted-foreground">
              See exactly how your customers will search. Try the live demo below.
            </p>
          </div>

          {/* Large Search Input */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              key={key}
              id="preview-search-input"
              type="search"
              placeholder="Try searching... (e.g., 'shoes', 'bleu dress', 'gift ideas')"
              className="pl-12 pr-4 h-14 text-lg rounded-xl border-2 focus-visible:ring-primary/20 focus-visible:ring-4"
              autoComplete="off"
            />
            {!isWidgetLoaded && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Quick tips */}
          <div className="flex flex-wrap justify-center gap-2 text-sm">
            <Badge variant="outline" className="bg-background/50">
              <Zap className="h-3 w-3 mr-1" />
              Try a typo like "sheos"
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <Eye className="h-3 w-3 mr-1" />
              Click eye icon for Quick View
            </Badge>
            <Badge variant="outline" className="bg-background/50">
              <TrendingUp className="h-3 w-3 mr-1" />
              See trending searches
            </Badge>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Powered by Smart Features</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <Card key={feature.title} className="border-muted">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <feature.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Configuration & Embed Code */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Customize Widget
            </CardTitle>
            <CardDescription>
              Adjust settings and see changes instantly
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2">
                  <Maximize2 className="h-4 w-4" />
                  Display Mode
                </Label>
                <p className="text-sm text-muted-foreground">
                  {mode === 'dropdown' ? 'Compact dropdown overlay' : 'Immersive fullscreen search'}
                </p>
              </div>
              <Switch
                checked={mode === 'fullscreen'}
                onCheckedChange={(checked) =>
                  setMode(checked ? 'fullscreen' : 'dropdown')
                }
              />
            </div>

            {/* Color Picker */}
            <div className="space-y-2">
              <Label htmlFor="primary-color">Brand Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primary-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-10 w-16 cursor-pointer p-1"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#3b82f6"
                  className="flex-1 font-mono"
                />
              </div>
            </div>

            {/* Quick Color Presets */}
            <div className="space-y-2">
              <Label>Quick Presets</Label>
              <div className="flex gap-2">
                {['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'].map((color) => (
                  <button
                    key={color}
                    className="h-8 w-8 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: color,
                      borderColor: primaryColor === color ? 'white' : 'transparent',
                      boxShadow: primaryColor === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                    onClick={() => setPrimaryColor(color)}
                  />
                ))}
              </div>
            </div>

            {/* Reinitialize Button */}
            <Button onClick={handleReinitialize} variant="outline" className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Preview
            </Button>

            {/* Widget Status */}
            <div className="flex items-center gap-2 text-sm pt-2 border-t">
              <div
                className={`h-2 w-2 rounded-full ${
                  isWidgetLoaded ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'
                }`}
              />
              <span className="text-muted-foreground">
                {isWidgetLoaded ? 'Widget ready' : 'Loading widget...'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Embed Code */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Copy className="h-5 w-5" />
                  Embed Code
                </CardTitle>
                <CardDescription>
                  Add this to your WooCommerce store
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyCode}
                className="gap-2"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto rounded-lg bg-muted p-4 text-sm font-mono">
              <code>{embedCode}</code>
            </pre>
            <p className="text-sm text-muted-foreground mt-4">
              Paste this code into your theme's header or footer. The widget will automatically
              enhance any search input on your site.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tips Section */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-lg">Pro Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-3 sm:grid-cols-2 text-sm">
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Search results appear instantly as you type (try "sh" for shoes)</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Typo tolerance finds products even with spelling mistakes</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Click the eye icon on any result to open Quick View</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Quick View shows similar products you might also like</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Your recent searches are saved for quick access</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>Configure synonyms, redirects, and boosts in the sidebar</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
